/**
 * Admin boundary layer module.
 *
 * Loads Rwanda admin boundary GeoJSON from static files and renders
 * them as line layers with zoom-dependent visibility.
 */

import type { Map } from 'mapbox-gl';
import type { FeatureCollection as GeoJsonFC } from 'geojson';
import { parseGeoJsonMaybe } from '../utils/geojson';

export type BoundaryLevel = 'none' | 'provinces' | 'districts' | 'sectors' | 'cells';

const BOUNDARY_FILES: Record<Exclude<BoundaryLevel, 'none'>, string> = {
  provinces: '/geodata/rwanda/rwanda_provinces.geojson',
  districts: '/geodata/rwanda/rwanda_districts.geojson',
  sectors: '/geodata/rwanda/rwanda_sectors.geojson',
  cells: '/geodata/rwanda/rwanda_cells.geojson',
};

const MIN_ZOOM: Record<BoundaryLevel, number> = {
  none: 0,
  provinces: 6,
  districts: 7,
  sectors: 10,
  cells: 12,
};

const BOUNDARY_STYLES: Record<string, { color: string; width: number; opacity: number }> = {
  cells: { color: '#64748B', width: 0.7, opacity: 0.55 },
  sectors: { color: '#0F766E', width: 1, opacity: 0.68 },
  districts: { color: '#2563EB', width: 1.6, opacity: 0.78 },
  provinces: { color: '#7C3AED', width: 2, opacity: 0.8 },
};

const LABEL_MIN_ZOOM: Record<Exclude<BoundaryLevel, 'none'>, number> = {
  provinces: 6,
  districts: 7,
  sectors: 10,
  cells: 13,
};

const cache: Partial<Record<string, GeoJsonFC>> = {};

async function loadBoundary(
  level: Exclude<BoundaryLevel, 'none'>,
): Promise<GeoJsonFC | null> {
  const key = level;
  if (cache[key]) return cache[key]!;

  try {
    const resp = await fetch(BOUNDARY_FILES[level]);
    if (!resp.ok) return null;
    const parsed = parseGeoJsonMaybe(await resp.json());
    if (!parsed) return null;
    const json = parsed as unknown as GeoJsonFC;
    cache[key] = json;
    return json;
  } catch {
    return null;
  }
}

function sourceId(level: string): string {
  return `admin-boundary-${level}`;
}

function layerId(level: string): string {
  return `admin-boundary-${level}-line`;
}

function labelLayerId(level: string): string {
  return `admin-boundary-${level}-labels`;
}

export function setAdminBoundaryLevel(
  map: Map,
  level: BoundaryLevel,
): void {
  // Remove all existing boundary layers
  for (const l of ['provinces', 'districts', 'sectors', 'cells']) {
    if (map.getLayer(labelLayerId(l))) map.removeLayer(labelLayerId(l));
    if (map.getLayer(layerId(l))) map.removeLayer(layerId(l));
    if (map.getSource(sourceId(l))) map.removeSource(sourceId(l));
  }

  if (level === 'none') return;

  const minZoom = MIN_ZOOM[level];

  loadBoundary(level).then((data) => {
    if (!data || !map.isStyleLoaded()) return;

    if (map.getSource(sourceId(level))) return;

    map.addSource(sourceId(level), {
      type: 'geojson',
      data,
    });

    const style = BOUNDARY_STYLES[level];
    map.addLayer({
      id: layerId(level),
      type: 'line',
      source: sourceId(level),
      minzoom: minZoom,
      layout: {},
      paint: {
        'line-color': style.color,
        'line-width': style.width,
        'line-opacity': style.opacity,
      },
    });

    map.addLayer({
      id: labelLayerId(level),
      type: 'symbol',
      source: sourceId(level),
      minzoom: LABEL_MIN_ZOOM[level],
      layout: {
        'symbol-placement': 'point',
        'text-field': ['coalesce', ['get', 'shapeName'], ['get', 'name'], ''],
        'text-size': ['interpolate', ['linear'], ['zoom'], LABEL_MIN_ZOOM[level], 10, 16, 13],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-allow-overlap': false,
        'text-ignore-placement': false,
      },
      paint: {
        'text-color': '#0F172A',
        'text-halo-color': 'rgba(255,255,255,0.94)',
        'text-halo-width': 1.5,
      },
    });
  });
}

export function removeAdminBoundaries(map: Map): void {
  for (const l of ['provinces', 'districts', 'sectors', 'cells']) {
    if (map.getLayer(labelLayerId(l))) map.removeLayer(labelLayerId(l));
    if (map.getLayer(layerId(l))) map.removeLayer(layerId(l));
    if (map.getSource(sourceId(l))) map.removeSource(sourceId(l));
  }
}
